import { createSseScope } from "utils/sse";

/**
 * Per-store ownership and cursor state for the one outline generation a user is watching.
 *
 * The binding names an execution attempt, not the outline: a regeneration is a different
 * attempt, and its events have their own ids. Carrying a checkpoint across that boundary would
 * resume a cursor into a run it never belonged to.
 */
export const createOutlineStreamService = () => {
  const scope = createSseScope();
  let binding = null;
  let checkpoint = null;
  let owners = 0;
  let currentOwner = 0;

  return {
    /**
     * Who the view belongs to, which is a longer-lived thing than any one stream.
     *
     * Kept apart from the observation lease on purpose: opening a stream replaces the previous
     * observation, and if the two shared a lease every open would invalidate the mount's own
     * claim — so the next guarded await would decide it had been navigated away from.
     */
    claimView: () => {
      owners += 1;
      currentOwner = owners;
      return owners;
    },
    ownsView: (owner) => owner === currentOwner,
    releaseView: (owner) => {
      if (owner === currentOwner) {
        currentOwner = 0;
      }
    },

    beginObservation: () => scope.begin(),
    cancel: (reason = "cancelled") => scope.abortCurrent(reason),
    dispose: () => scope.dispose(),

    currentBinding: () => binding,

    /**
     * Binds to an attempt, keeping the cursor unless this is provably a different one.
     *
     * A stream opened by alias does not know its run id until a handshake supplies one, so
     * learning the id of the attempt already being watched must not be mistaken for a change of
     * attempt — that would throw away a cursor and replay the run from the beginning.
     */
    bindTo(next) {
      const differentFlow = !binding || binding.flowId !== next.flowId;
      const differentRun =
        binding && binding.runId && next.runId && binding.runId !== next.runId;
      if (differentFlow || differentRun) {
        checkpoint = null;
      }
      // A run id belongs to the flow that issued it, so only an unchanged flow can supply one the
      // incoming binding omits. Inheriting across a flow change asks the new outline for the
      // previous outline's attempt.
      const inheritedRun = differentFlow ? null : (binding?.runId ?? null);
      binding = { ...next, runId: next.runId ?? inheritedRun };
      return binding;
    },

    startNewAttempt(next) {
      checkpoint = null;
      binding = next;
      return binding;
    },

    checkpoint: () => checkpoint,
    recordCheckpoint(next) {
      if (next) {
        checkpoint = next;
      }
    },

    reset() {
      binding = null;
      checkpoint = null;
    },
  };
};
